/**
 * Message type helper utilities
 */

import { MessageType } from '@/types/enums';

export interface MessageTypeInfo {
  type: MessageType;
  color: string;
  label: string;
  icon: string;
  description: string;
}

/**
 * Get message type info (color, label, icon)
 */
export const getMessageTypeInfo = (type: MessageType): MessageTypeInfo => {
  const infoMap: Record<MessageType, MessageTypeInfo> = {
    [MessageType.TITLE]: {
      type: MessageType.TITLE,
      color: '#2196f3', // Blue
      label: 'Title',
      icon: '🔵',
      description: 'Message contains manuscript title information',
    },
    [MessageType.MEDIA]: {
      type: MessageType.MEDIA,
      color: '#9c27b0', // Purple
      label: 'Media',
      icon: '🟣',
      description: 'Single media file (PDF, image, etc.)',
    },
    [MessageType.ALBUM]: {
      type: MessageType.ALBUM,
      color: '#673ab7', // Deep purple
      label: 'Album',
      icon: '📚',
      description: 'Grouped media files (album)',
    },
    [MessageType.SEPARATOR]: {
      type: MessageType.SEPARATOR,
      color: '#9e9e9e', // Gray
      label: 'Separator',
      icon: '⚪',
      description: 'Visual separator between entries',
    },
    [MessageType.NOISE]: {
      type: MessageType.NOISE,
      color: '#757575', // Dark gray
      label: 'Noise',
      icon: '🔘',
      description: 'Irrelevant message (noise)',
    },
    [MessageType.REPLY_STUB]: {
      type: MessageType.REPLY_STUB,
      color: '#bdbdbd', // Light gray
      label: 'Reply',
      icon: '💬',
      description: 'Reply to a message',
    },
    [MessageType.UNKNOWN]: {
      type: MessageType.UNKNOWN,
      color: '#ff9800', // Orange
      label: 'Unknown',
      icon: '⚠️',
      description: 'Unclassified message type',
    },
  };

  return infoMap[type] || infoMap[MessageType.UNKNOWN];
};

/**
 * Get all message types for filter dropdown
 */
export const getAllMessageTypes = (): MessageType[] => {
  return Object.values(MessageType);
};

/**
 * Get message type label for display
 */
export const getMessageTypeLabel = (type: MessageType): string => {
  return getMessageTypeInfo(type).label;
};

/**
 * Get message type color
 */
export const getMessageTypeColor = (type: MessageType): string => {
  return getMessageTypeInfo(type).color;
};
